<template>
  <div class="review-and-pay-step">
    <ReviewAndPayCard
      :recap-title="t(`billing.compare.planName.${plan}`)"
      :recap-subtitle="recapSubtitle"
      :recap-icon="planIcon"
      :recap-lines="recapLines"
      :total-label="t('billing.review.totalToday')"
      :total-value="totalValueLabel"
      :payment-mode="paymentMode"
      :payment-method-id="paymentMethodId"
      :default-card="defaultCard"
      :all-cards="allCards"
      :amount="totalAmount"
      :currency="currency"
      :allowed-methods="ALLOWED_METHODS_PLAN"
      :dropin-submit-handler="onDropinSubmit"
      :dropin-details-handler="onDropinDetails"
      :payment-error="paymentError"
      :cta-label="ctaLabel"
      :cta-icon="ctaIcon"
      :cta-submitting="submitting"
      :cta-disabled="!isPurePlanDowngrade && (loading || !preview)"
      :trust-text="t('billing.review.trustSecure')"
      @update:payment-mode="paymentMode = $event"
      @update:payment-method-id="paymentMethodId = $event"
      @dropin-success="onDropinSuccess"
      @dropin-error="onDropinError"
      @cta-click="onPay"
    >
      <template #recap-extra>
        <!-- Loading skeleton inside the recap column. -->
        <div
          v-if="loading"
          class="review-and-pay-step__skeleton"
        >
          <div class="review-and-pay-step__skeleton-line" />
          <div class="review-and-pay-step__skeleton-line" />
          <div class="review-and-pay-step__skeleton-line review-and-pay-step__skeleton-line--big" />
        </div>
        <Message
          v-else-if="errorMessage"
          severity="error"
          :closable="false"
          class="mt-2"
        >
          {{ errorMessage }}
        </Message>
        <div
          v-else-if="preview?.nextRenewalDate"
          class="review-and-pay-step__next"
        >
          <i class="fa-regular fa-calendar" />
          <span>{{ t('billing.review.nextRenewal', { date: formattedNextRenewal }) }}</span>
        </div>
      </template>

      <!-- Downgrade / Shopify flows : no Adyen, the card surfaces an
           explanatory block instead of the PaymentBlock. The in-card CTA
           keeps doing the right thing (scheduleDowngrade vs subscribe). -->
      <template
        v-if="isPurePlanDowngrade || isShopify"
        #payment-content
      >
        <div
          v-if="isPurePlanDowngrade"
          class="review-and-pay-step__downgrade"
        >
          <div class="review-and-pay-step__downgrade-icon">
            <i class="fa-regular fa-clock" />
          </div>
          <div class="review-and-pay-step__downgrade-info">
            <div class="review-and-pay-step__downgrade-title">
              {{ t('billing.review.downgradeTitle') }}
            </div>
            <p class="review-and-pay-step__downgrade-text">
              {{ t('billing.review.downgradeText', {
                current: currentPlan ? t(`billing.compare.planName.${currentPlan}`) : '',
                next: t(`billing.compare.planName.${plan}`),
                date: formattedNextRenewal,
              }) }}
            </p>
          </div>
        </div>
        <div
          v-else-if="isShopify"
          class="review-and-pay-step__shopify"
        >
          <div class="review-and-pay-step__shopify-icon">
            <i class="fa-brands fa-shopify" />
          </div>
          <p class="review-and-pay-step__shopify-text">
            {{ t('billing.payment.shopify.notice') }}
          </p>
        </div>
      </template>
    </ReviewAndPayCard>
  </div>
</template>

<script lang="ts">
import {
  computed, defineComponent, PropType, ref, watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from '@/store';
import Message from 'primevue/message';
import moment from 'moment';
import ReviewAndPayCard, { ReviewAndPayLine } from '@/components/billing/shared/ReviewAndPayCard.vue';
import {
  ActionResult,
  ALLOWED_METHODS_PLAN,
  confirmPlan,
  downgradeNextPlan,
  previewPlan,
  subscribePlan,
} from '@/composables/billing/billingApi';
import { useStructuralReload } from '@/composables/billing/useStructuralReload';
import { PaymentMethodItem } from '@/types/billing-types';

const PLAN_RANK: Record<string, number> = {
  standard: 1,
  premium: 2,
  ultimate: 3,
  pro: 4,
};

type PaymentMode = 'existing' | 'new';

/**
 * Plan subscription / change-plan review step — the last step of
 * `ChangePlanDialog`. Thin orchestrator around {@link ReviewAndPayCard} :
 * builds the recap from `previewPlan(...)` and routes the CTA to either
 * `subscribePlan` (upgrade / new plan) or `downgradeNextPlan` (downgrade).
 *
 * Three modes share the same shell :
 *   - Normal upgrade / new plan : recap + PaymentBlock + Adyen flow.
 *   - Downgrade                 : recap + « scheduled at next renewal »
 *                                  card (replaces PaymentBlock) + CTA that
 *                                  schedules instead of charging.
 *   - Shopify shop              : recap + Shopify notice + CTA that triggers
 *                                  the subscribe flow which redirects to the
 *                                  Shopify approval URL.
 */
export default defineComponent({
  name: 'ReviewAndPayStep',

  components: { Message, ReviewAndPayCard },

  props: {
    plan: {
      type: String as PropType<string>,
      required: true,
    },
    frequency: {
      type: String as PropType<'monthly' | 'yearly'>,
      required: true,
    },
    targetTier: {
      type: Number as PropType<number | undefined>,
      default: undefined,
    },
    smsInclusion: {
      type: Number as PropType<number>,
      default: 0,
    },
    /**
     * Current active plan code, used to detect downgrades. When the
     * selected plan ranks lower than this one, we switch to a
     * « schedule for next renewal » flow (no immediate payment).
     */
    currentPlan: {
      type: String as PropType<string | null>,
      default: null,
    },
    /**
     * V5 auto-tier-upgrade configuration (forwarded to `subscribePlan`).
     * Ignored for V4 — these props default to undefined so the back keeps
     * the legacy behaviour (no auto-upgrade fields written).
     */
    tierAutoUpgradeEnabled: {
      type: Boolean as PropType<boolean | undefined>,
      default: undefined,
    },
    tierAutoUpgradeMax: {
      type: Number as PropType<number | undefined>,
      default: undefined,
    },
  },

  emits: ['paid', 'shopify-redirect'],

  setup(props, { emit }) {
    const { t } = useI18n();
    const store = useStore();
    const handleReload = useStructuralReload();

    const preview = ref<any | null>(null);
    const loading = ref(false);
    const errorMessage = ref<string | null>(null);
    const paymentError = ref<string | null>(null);
    const submitting = ref(false);
    const paymentMode = ref<PaymentMode>('existing');
    const paymentMethodId = ref<string | null>(null);

    const overview = computed(() => store.state.billing.overview);
    const isShopify = computed(() => overview.value?.shop?.solutionEcommerce === 'shopify');
    const currency = computed<'EUR' | 'USD'>(
      () => (overview.value?.shop?.currency === 'USD' ? 'USD' : 'EUR'),
    );
    const allCards = computed<PaymentMethodItem[]>(
      () => overview.value?.paymentMethods?.items ?? [],
    );
    const defaultCard = computed(
      () => allCards.value.find((p: any) => p.isDefault) ?? null,
    );
    const hasDefaultCard = computed(() => defaultCard.value !== null);

    const planIcon = computed(() => {
      const code = props.plan;
      if (code === 'standard') return 'fa-regular fa-seedling';
      if (code === 'premium') return 'fa-regular fa-rocket';
      if (code === 'ultimate') return 'fa-regular fa-star';
      if (code === 'pro') return 'fa-regular fa-crown';
      return 'fa-regular fa-circle';
    });

    const formatNumber = (n: number) => new Intl.NumberFormat('fr-FR').format(n);

    const recapSubtitle = computed(() => {
      const freq = props.frequency === 'yearly'
        ? t('billing.compare.yearly')
        : t('billing.compare.monthly');
      if (props.targetTier) {
        return `${freq} · ${t('billing.review.tierLabel', { count: formatNumber(props.targetTier) })}`;
      }
      return freq;
    });

    const formattedNextRenewal = computed(() => (
      preview.value?.nextRenewalDate
        ? moment(preview.value.nextRenewalDate).format('D MMMM YYYY')
        : '—'
    ));

    // Downgrade detection : if the selected plan ranks strictly lower than
    // the currently active plan, we don't bill the user immediately — we
    // schedule the change at the next renewal via /plans/downgrade-next.
    const isDowngrade = computed(() => {
      if (!props.currentPlan || !props.plan) return false;
      const current = PLAN_RANK[props.currentPlan] ?? 0;
      const next = PLAN_RANK[props.plan] ?? 0;
      return current > 0 && next > 0 && next < current;
    });

    const recapLines = computed<ReviewAndPayLine[]>(() => {
      if (!preview.value) return [];
      const lines: ReviewAndPayLine[] = [];

      // 1. Catalog price — informational header (the monthly or yearly
      //    sticker price the customer will pay at each renewal).
      lines.push({
        label: t('billing.review.baseAmount'),
        value: preview.value.baseAmount?.label ?? '—',
      });

      // 2. Prorata for the remaining days. Shown only when it diverges from
      //    the catalog (i.e., mid-cycle). The label carries the day counts
      //    so the customer understands how the number was derived.
      const days = preview.value.breakdown?.daysRemaining;
      const total = preview.value.breakdown?.daysInCurrentCycle;
      if (
        preview.value.prorataAmount
        && preview.value.prorataAmount.amount !== preview.value.baseAmount?.amount
      ) {
        lines.push({
          label: days != null && total != null && days > 0
            ? t('billing.review.prorataWithDays', { days, total })
            : t('billing.review.prorata'),
          value: preview.value.prorataAmount.label,
          kind: 'accent',
        });
      }

      // 3. Credit from the unconsumed part of the current cycle. New field
      //    introduced by the proration refactor — this is the fix for the
      //    long-standing upgrade overcharge.
      if (
        preview.value.creditFromCurrent
        && preview.value.creditFromCurrent.amount > 0
      ) {
        lines.push({
          label: days != null && total != null && days > 0
            ? t('billing.review.creditFromCurrentWithDays', { days, total })
            : t('billing.review.creditFromCurrent'),
          value: `− ${preview.value.creditFromCurrent.label}`,
          kind: 'discount',
        });
      }

      // 3bis. SMS subscription line (no prorata — absolute difference).
      //       The smsPlan is not prorated when changing offer: the current
      //       pack stays valid until renewal, the new amount applies then.
      //       We only invoice the increase, if any. Three display cases:
      //         a) smsCharge > 0 → "Augmentation SMS plan : +X €"
      //         b) smsCharge = 0 and target === current → no line (silent)
      //         c) smsCharge = 0 and target < current (decrease/disable) →
      //            info line "SMS plan modifié — aucun débit, effectif au
      //            prochain renouvellement"
      const sms = preview.value.breakdown;
      const smsCharge = sms?.smsCharge;
      const currentSms = sms?.currentSmsAmount?.amount ?? 0;
      const targetSms = sms?.targetSmsAmount?.amount ?? 0;
      if (smsCharge && smsCharge.amount > 0) {
        lines.push({
          label: t('billing.review.smsIncrease'),
          value: `+ ${smsCharge.label}`,
          kind: 'accent',
        });
      } else if (currentSms > 0 && targetSms < currentSms) {
        lines.push({
          label: t('billing.review.smsScheduledChange'),
          value: '—',
          kind: 'discount',
        });
      }

      // 4. Plan discount (% or fixed price).
      if (preview.value.discountApplied) {
        const d = preview.value.discountApplied;
        lines.push({
          label: d.kind === 'percent'
            ? t('billing.review.discountPercent', { value: d.value })
            : t('billing.review.discountFixed'),
          value: `− ${d.amountSaved?.label ?? ''}`,
          kind: 'discount',
        });
      }

      // 5. SMS inclusion charged in the same invoice.
      if (
        preview.value.smsInclusionAmount
        && preview.value.smsInclusionAmount.amount > 0
      ) {
        lines.push({
          label: t('billing.review.creditsAdded'),
          value: `+ ${preview.value.smsInclusionAmount.label}`,
          kind: 'accent',
        });
      }
      return lines;
    });

    const totalValueLabel = computed(() => preview.value?.totalToPay?.label ?? '—');
    const totalAmount = computed(() => preview.value?.totalToPay?.amount ?? 0);
    // When the prorata math comes out to zero (credit fully covers the new
    // plan), the CTA reads "Confirmer" and the icon is a check. The call
    // still hits subscribePlan → Adyen → webhook (no bypass) — Adyen handles
    // 0-EUR auths and the webhook applies the plan, same as paid upgrades.
    const isFreeChange = computed(() => totalAmount.value === 0 && !isPurePlanDowngrade.value);

    const payButtonLabel = computed(() => {
      if (isFreeChange.value) return t('billing.review.confirmCta');
      const amount = preview.value?.totalToPay?.label ?? '';
      return amount
        ? t('billing.review.payCta', { amount })
        : t('billing.review.payCtaGeneric');
    });

    const ctaLabel = computed(() => {
      if (isPurePlanDowngrade.value) {
        return t('billing.review.downgradeCta', {
          plan: t(`billing.compare.planName.${props.plan}`),
        });
      }
      if (isShopify.value) return t('billing.payment.shopify.cta');
      // For Adyen flows the CTA only shows when we have a default card AND
      // the user is in `existing` mode — ReviewAndPayCard handles the
      // visibility, so we always provide the label here.
      return hasDefaultCard.value ? payButtonLabel.value : '';
    });

    const ctaIcon = computed(() => {
      if (isPurePlanDowngrade.value) return 'fa-regular fa-clock';
      if (isShopify.value) return 'fa-brands fa-shopify';
      if (isFreeChange.value) return 'fa-regular fa-circle-check';
      return 'fa-regular fa-lock';
    });

    const load = async () => {
      // Always load the preview — even on a plan downgrade. The preview
      // tells us the actual `totalToPay`, which may be non-zero when the
      // customer bumped up their SMS subscription at the same time. Spec
      // metier: SMS delta is invoiced immediately even if the plan downgrade
      // itself is scheduled for the next renewal.
      loading.value = true;
      errorMessage.value = null;
      try {
        const result = await previewPlan({
          plan: props.plan,
          frequency: props.frequency,
          targetTier: props.targetTier,
          smsInclusion: props.smsInclusion,
        });
        if (!result) {
          errorMessage.value = t('billing.preview.cannotLoad');
          preview.value = null;
        } else {
          preview.value = result;
        }
      } finally {
        loading.value = false;
      }
    };

    /**
     * `true` when the only thing to do is schedule a plan downgrade (no
     * immediate charge). Computed from the preview's `totalToPay`: if zero
     * and the user picked a lower-rank plan, we route to `downgradeNextPlan`
     * instead of `subscribePlan`. When `totalToPay > 0` (typically because
     * the customer also bumped up their SMS subscription), `subscribePlan`
     * handles both the SMS billing AND scheduling the plan downgrade.
     */
    const isPurePlanDowngrade = computed(() => (
      isDowngrade.value
      && (preview.value?.totalToPay?.amount ?? 0) === 0
    ));

    const onPay = async () => {
      paymentError.value = null;
      submitting.value = true;
      try {
        // Pure plan downgrade (no SMS delta to invoice today) — schedule
        // only, no payment. When the customer also raised the SMS plan,
        // `totalToPay > 0` and we fall through to the subscribe flow which
        // bills the SMS and schedules the plan in one call.
        //
        // We always forward `selectedSmsInclusion` as `nextSmsPlan` so the
        // user's choice at the AddCreditsStep — including the explicit « no
        // SMS at next renewal » (smsInclusion=0) — is persisted in
        // `plan.nextSmsPlan` and consumed by the renewal worker. Previously
        // this path dropped the SMS choice entirely.
        if (isPurePlanDowngrade.value) {
          const result = await downgradeNextPlan({
            nextPlan: props.plan,
            nextSmsPlan: props.smsInclusion,
          });
          await handleReload(result, {
            onFailure: (err) => {
              paymentError.value = err;
            },
          });
          if (result.status === 'completed') emit('paid');
          return;
        }

        // Upgrade / new plan : regular subscribe flow.
        const result = await subscribePlan({
          plan: props.plan,
          frequency: props.frequency,
          targetTier: props.targetTier,
          smsInclusion: props.smsInclusion,
          ...(props.tierAutoUpgradeEnabled !== undefined
            ? { tierAutoUpgradeEnabled: props.tierAutoUpgradeEnabled }
            : {}),
          ...(props.tierAutoUpgradeMax !== undefined && props.tierAutoUpgradeMax > 0
            ? { tierAutoUpgradeMax: props.tierAutoUpgradeMax }
            : {}),
          ...(paymentMethodId.value
            ? { paymentMethodId: paymentMethodId.value }
            : {}),
        });
        // `completed` (carte enregistrée / token) : comme pour la nouvelle
        // carte, le plan est posé par le webhook Adyen asynchrone. On délègue
        // le rafraîchissement à @paid → onPlanSubscribed (poll + reload) au
        // lieu du reload immédiat de handleReload, qui courrait le webhook et
        // rechargerait sur « Aucun plan actif ». Les autres statuts (échec /
        // 3DS / Shopify) restent gérés par handleReload.
        if (result.status === 'completed') {
          emit('paid');
        } else {
          await handleReload(result, {
            onFailure: (err) => {
              paymentError.value = err;
            },
            onShopifyApproval: (confirmationUrl) => {
              emit('shopify-redirect', confirmationUrl);
              window.location.href = confirmationUrl;
            },
            onActionRequired: () => {
              paymentError.value = t('billing.payment.actionRequired');
            },
          });
        }
      } finally {
        submitting.value = false;
      }
    };

    // ─── Drop-in (new card) handlers ─────────────────────────────────────
    const onDropinSubmit = (
      adyenStateData: Record<string, any>,
    ): Promise<ActionResult> => subscribePlan({
      plan: props.plan,
      frequency: props.frequency,
      targetTier: props.targetTier,
      smsInclusion: props.smsInclusion,
      ...(props.tierAutoUpgradeEnabled !== undefined
        ? { tierAutoUpgradeEnabled: props.tierAutoUpgradeEnabled }
        : {}),
      ...(props.tierAutoUpgradeMax !== undefined && props.tierAutoUpgradeMax > 0
        ? { tierAutoUpgradeMax: props.tierAutoUpgradeMax }
        : {}),
      adyenStateData,
    });

    const onDropinDetails = (
      adyenStateData: Record<string, any>,
    ): Promise<ActionResult> => confirmPlan({ adyenStateData });

    const onDropinSuccess = () => {
      // Le plan est appliqué par le webhook Adyen AUTHORISATION (asynchrone).
      // On délègue le rafraîchissement au parent (SubscriptionPage →
      // onPlanSubscribed) qui poll l'overview jusqu'à l'application du plan
      // puis recharge la page. Un refetch immédiat ici courrait le webhook et
      // laisserait « Aucun plan actif » jusqu'au prochain F5.
      emit('paid');
    };

    const onDropinError = (err: string) => {
      paymentError.value = err;
    };

    watch(
      () => [props.plan, props.frequency, props.targetTier, props.smsInclusion],
      () => load(),
      { immediate: true },
    );

    watch(
      hasDefaultCard,
      (has) => {
        paymentMode.value = has ? 'existing' : 'new';
      },
      { immediate: true },
    );

    return {
      t,
      ALLOWED_METHODS_PLAN,
      preview,
      loading,
      errorMessage,
      paymentError,
      submitting,
      paymentMode,
      paymentMethodId,
      isShopify,
      currency,
      allCards,
      defaultCard,
      planIcon,
      recapSubtitle,
      recapLines,
      totalValueLabel,
      totalAmount,
      formattedNextRenewal,
      isDowngrade,
      isPurePlanDowngrade,
      ctaLabel,
      ctaIcon,
      onPay,
      onDropinSubmit,
      onDropinDetails,
      onDropinSuccess,
      onDropinError,
    };
  },
});
</script>

<style lang="scss" scoped>
@import "@/assets/scss/utilities/variables";

.review-and-pay-step {
  // ─── Loading skeleton (recap column) ─────────────────────────────────
  &__skeleton {
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin: 4px 0 16px;
  }

  &__skeleton-line {
    height: 14px;
    background: linear-gradient(90deg, #eef2f7 0%, #f8fafc 50%, #eef2f7 100%);
    background-size: 200% 100%;
    border-radius: 6px;
    animation: review-and-pay-step-shimmer 1400ms linear infinite;

    &--big { height: 26px; }
  }

  // ─── Next renewal note (in recap-extra slot) ─────────────────────────
  &__next {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    margin-top: 14px;
    font-size: 0.8125rem;
    color: #64748b;

    i { color: #94a3b8; }
  }

  // ─── Downgrade scheduled card (in payment-content slot) ──────────────
  &__downgrade {
    display: flex;
    gap: 14px;
    padding: 18px;
    background: linear-gradient(180deg, #f8faff 0%, #ffffff 100%);
    border: 1px solid #dbe5fb;
    border-radius: 12px;
  }

  &__downgrade-icon {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    background: #1d4ed8;
    color: #fff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  &__downgrade-title {
    font-weight: 700;
    color: #0f172a;
    font-size: 0.9375rem;
  }

  &__downgrade-text {
    margin: 4px 0 0;
    font-size: 0.8125rem;
    color: #475569;
    line-height: 1.5;
  }

  // ─── Shopify notice (in payment-content slot) ────────────────────────
  &__shopify {
    display: flex;
    gap: 14px;
    align-items: center;
    padding: 18px;
    background: linear-gradient(135deg, #f7fbf2 0%, #ffffff 100%);
    border: 1px solid #ecf3e3;
    border-radius: 12px;
  }

  &__shopify-icon {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    background: #95bf47;
    color: #fff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 1.25rem;
    flex-shrink: 0;
  }

  &__shopify-text {
    margin: 0;
    font-size: 0.8125rem;
    color: #475569;
    line-height: 1.5;
  }
}

@keyframes review-and-pay-step-shimmer {
  0%   { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}
</style>
